/**
 * LLM Email Filter Service (Secure Version)
 *
 * Secure microservice architecture - calls Supabase Edge Function
 * API keys stay server-side, authenticated requests only
 */

import { supabase } from './supabaseClient';

// ============================================================================
// TIER CONFIGURATIONS
// ============================================================================

// FREE TIER: Small batches, model rotation on rate limits (current behavior)
const FREE_TIER_CONFIG = {
  mode: 'free',
  batchSize: 2,
  rotateOnRateLimit: true,
  models: [
    { name: 'llama-3.1-8b-instant', tokensPerMinute: 6000, tokensPerRequest: 6000 },
    { name: 'llama-3.3-70b-versatile', tokensPerMinute: 12000, tokensPerRequest: 12000 },
    { name: 'openai/gpt-oss-20b', tokensPerMinute: 8000, tokensPerRequest: 8000 },
    { name: 'openai/gpt-oss-120b', tokensPerMinute: 8000, tokensPerRequest: 8000 }
  ]
};

// PAID TIER: Large batches (5-10 emails), single model, no rotation
const PAID_TIER_CONFIG = {
  mode: 'paid',
  batchSize: 10,
  maxBatchTokens: 12000,
  rotateOnRateLimit: false,
  selectedModel: 'llama-3.3-70b-versatile'
};

// ALL available Groq models for comparison test (ranked by cost - cheapest first)
// Model IDs from https://console.groq.com/docs/models
// maxOutputTokens varies by model - some have lower limits
const ALL_GROQ_MODELS = [
  // Production models
  { name: 'llama-3.1-8b-instant', inputCost: 0.05, outputCost: 0.08, context: '128k', maxOutputTokens: 8192, status: 'production' },
  { name: 'openai/gpt-oss-20b', inputCost: 0.075, outputCost: 0.30, context: '128k', maxOutputTokens: 16000, status: 'production' },
  { name: 'openai/gpt-oss-120b', inputCost: 0.15, outputCost: 0.60, context: '128k', maxOutputTokens: 16000, status: 'production' },
  { name: 'llama-3.3-70b-versatile', inputCost: 0.59, outputCost: 0.79, context: '128k', maxOutputTokens: 32768, status: 'production' },
  // Preview models (may have limited availability)
  { name: 'openai/gpt-oss-safeguard-20b', inputCost: 0.075, outputCost: 0.30, context: '128k', maxOutputTokens: 16000, status: 'preview' },
  { name: 'meta-llama/llama-4-scout-17b-16e-instruct', inputCost: 0.11, outputCost: 0.34, context: '128k', maxOutputTokens: 8192, status: 'preview' },
  { name: 'meta-llama/llama-4-maverick-17b-128e-instruct', inputCost: 0.20, outputCost: 0.60, context: '128k', maxOutputTokens: 8192, status: 'preview' },
  { name: 'qwen/qwen3-32b', inputCost: 0.29, outputCost: 0.59, context: '131k', maxOutputTokens: 16000, status: 'preview' },
  { name: 'moonshotai/kimi-k2-instruct-0905', inputCost: 1.00, outputCost: 3.00, context: '256k', maxOutputTokens: 16000, status: 'preview' }
  // Removed llama-guard-4-12b - it's a safety model, not for classification (max 1024 tokens)
];

// Current tier mode - controlled by admin toggle
let currentTierMode = 'free'; // 'free' | 'paid'
let selectedPaidModel = PAID_TIER_CONFIG.selectedModel;

// ============================================================================
// TIER MODE API
// ============================================================================

/**
 * Get current tier mode
 */
export const getTierMode = () => currentTierMode;

/**
 * Set tier mode (admin only)
 */
export const setTierMode = (mode) => {
  if (mode !== 'free' && mode !== 'paid') {
    throw new Error('Invalid tier mode. Use "free" or "paid"');
  }
  currentTierMode = mode;
  console.log(`🔧 Tier mode switched to: ${mode}`);
};

/**
 * Get selected paid model
 */
export const getSelectedPaidModel = () => selectedPaidModel;

/**
 * Set selected paid model (admin only)
 */
export const setSelectedPaidModel = (modelName) => {
  const validModel = ALL_GROQ_MODELS.find(m => m.name === modelName);
  if (!validModel) {
    throw new Error(`Invalid model: ${modelName}`);
  }
  selectedPaidModel = modelName;
  console.log(`🤖 Paid model set to: ${modelName}`);
};

/**
 * Get all available models for comparison
 */
export const getAllGroqModels = () => ALL_GROQ_MODELS;

/**
 * Get current tier config
 */
export const getCurrentTierConfig = () => {
  return currentTierMode === 'paid' ? PAID_TIER_CONFIG : FREE_TIER_CONFIG;
};

// ============================================================================
// LEGACY CONFIG (for backward compatibility)
// ============================================================================

// Configuration
const CONFIG = {
  batchSize: parseInt(process.env.REACT_APP_LLM_BATCH_SIZE || '2'),
  confidenceThreshold: parseInt(process.env.REACT_APP_LLM_CONFIDENCE_THRESHOLD || '70'),
  maxRetries: 3,
  retryDelayBase: 2000, // 2 seconds base delay for exponential backoff (2s, 4s, 8s) - only if ALL models fail
  batchDelay: 0, // No delay needed - we rotate models instead
  edgeFunctionUrl: `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/classify-emails`,
  useDirectGroq: process.env.REACT_APP_USE_DIRECT_GROQ === 'true',
  groqApiKey: process.env.REACT_APP_GROQ_API_KEY || '',
  groqApiUrl: 'https://api.groq.com/openai/v1/chat/completions',

  // Model rotation priority list with token limits (free tier)
  // Using only verified working production models from https://console.groq.com/docs/models
  models: FREE_TIER_CONFIG.models
};

// Track current model index for rotation
let currentModelIndex = 0;

/**
 * Estimate token count for text
 * Rule of thumb: ~4 characters = 1 token for English text
 * This is a conservative estimate to avoid hitting limits
 */
const estimateTokens = (text) => {
  if (!text) return 0;
  return Math.ceil(text.length / 3.5); // Slightly aggressive to be safe
};

/**
 * Estimate tokens for system + user messages
 */
const estimatePromptTokens = (emails) => {
  const systemTokens = estimateTokens(SYSTEM_MESSAGE);
  const userTokens = estimateTokens(buildUserMessage(emails));
  return systemTokens + userTokens;
};

// Debug: Log configuration on load
console.log('🔧 LLM Service Configuration:', {
  useDirectGroq: CONFIG.useDirectGroq,
  hasGroqApiKey: !!CONFIG.groqApiKey,
  groqApiKeyLength: CONFIG.groqApiKey?.length || 0,
  batchSize: CONFIG.batchSize,
  confidenceThreshold: CONFIG.confidenceThreshold,
  envVars: {
    REACT_APP_USE_DIRECT_GROQ: process.env.REACT_APP_USE_DIRECT_GROQ,
    REACT_APP_GROQ_API_KEY_SET: !!process.env.REACT_APP_GROQ_API_KEY
  }
});

/**
 * Format Gmail message to complete JSON structure for LLM
 * Preserves ALL fields from the email for context
 */
export const formatEmailForLLM = (gmailMessage) => {
  try {
    const headers = gmailMessage.payload.headers || [];

    // Helper to get header value
    const getHeader = (name) => {
      const header = headers.find(h => h.name.toLowerCase() === name.toLowerCase());
      return header ? header.value : '';
    };

    // Extract email body
    const getEmailBody = (message) => {
      let plainText = '';
      let htmlText = '';

      const decodeBase64 = (str) => {
        try {
          let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
          while (base64.length % 4) base64 += '=';
          return atob(base64);
        } catch (error) {
          return '';
        }
      };

      const extractParts = (part) => {
        if (part.mimeType === 'text/plain' && part.body.data) {
          plainText += decodeBase64(part.body.data);
        } else if (part.mimeType === 'text/html' && part.body.data) {
          htmlText += decodeBase64(part.body.data);
        }

        if (part.parts) {
          part.parts.forEach(extractParts);
        }
      };

      if (message.payload.parts) {
        message.payload.parts.forEach(extractParts);
      } else if (message.payload.body.data) {
        const decoded = decodeBase64(message.payload.body.data);
        if (message.payload.mimeType === 'text/html') {
          htmlText = decoded;
        } else {
          plainText = decoded;
        }
      }

      return { plainText, htmlText };
    };

    const body = getEmailBody(gmailMessage);

    // Keep FULL body content for accurate classification
    let bodyForLLM = body.plainText || body.htmlText || '';

    // AGGRESSIVE text cleaning - remove ALL HTML and special characters
    // Always clean, regardless of whether it's plain text or HTML
    bodyForLLM = bodyForLLM
      // Remove script and style tags completely (with content)
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')

      // Remove HTML comments
      .replace(/<!--[\s\S]*?-->/g, '')

      // Convert common block elements to line breaks BEFORE removing tags
      .replace(/<\/?(div|p|br|h[1-6]|tr|li|td|th)[^>]*>/gi, '\n')
      .replace(/<\/?(table|tbody|thead|tfoot|section|article|header|footer)[^>]*>/gi, '\n')

      // Remove ALL remaining HTML tags (anything between < and >)
      .replace(/<[^>]+>/g, '')

      // Decode HTML entities
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&#\d+;/g, '')  // Remove numeric entities
      .replace(/&[a-zA-Z]+;/g, '')  // Remove named entities

      // Remove URLs (they're not useful for classification)
      .replace(/https?:\/\/[^\s]+/gi, '')
      .replace(/www\.[^\s]+/gi, '')

      // Remove email addresses
      .replace(/[\w.-]+@[\w.-]+\.\w+/g, '')

      // Remove special characters that aren't useful
      .replace(/[<>{}[\]\\|`~]/g, '')

      // Replace \r\n, \r with \n
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')

      // Remove zero-width spaces and invisible Unicode
      .replace(/[\u200B-\u200D\uFEFF]/g, '')

      // Remove control characters (except newlines and tabs)
      .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')

      // Remove UTF-8 encoding artifacts (common in mangled HTML)
      .replace(/[âÂ][ÍÌÎÏíìîï\u0080-\u009F]/g, '')  // âÍ, Â­, etc.
      .replace(/â€[™"\u0093\u0094\u0098\u0099]/g, '')  // Smart quotes/dashes
      .replace(/[•·]/g, '')  // Bullet points

      // Remove repeated non-letter characters (like "────" or "====")
      .replace(/([^\w\s])\1{3,}/g, '')

      // Remove leading/trailing whitespace per line
      .replace(/^[ \t]+|[ \t]+$/gm, '')

      // Remove empty lines
      .split('\n')
      .filter(line => line.trim().length > 0)
      .join('\n')

      // Collapse multiple spaces/tabs to single space
      .replace(/[ \t]+/g, ' ')

      // Collapse multiple newlines to single newline
      .replace(/\n+/g, '\n')

      // Final trim
      .trim();

    // Truncate body to save tokens - Keep first 2000 chars (subject/from/to already contain key info)
    // LLM doesn't need the full promotional content to classify subscription emails
    const MAX_BODY_LENGTH = 2000;
    if (bodyForLLM.length > MAX_BODY_LENGTH) {
      bodyForLLM = bodyForLLM.substring(0, MAX_BODY_LENGTH) + '... [truncated]';
    }

    // Extract attachments info
    const attachments = [];
    const extractAttachments = (part) => {
      if (part.filename && part.filename.length > 0) {
        attachments.push({
          filename: part.filename,
          mimeType: part.mimeType,
          size: part.body.size
        });
      }
      if (part.parts) {
        part.parts.forEach(extractAttachments);
      }
    };

    if (gmailMessage.payload.parts) {
      gmailMessage.payload.parts.forEach(extractAttachments);
    }

    // Build complete email object optimized for LLM
    return {
      id: gmailMessage.id,
      threadId: gmailMessage.threadId,
      subject: getHeader('subject'),
      from: getHeader('from'),
      to: getHeader('to'),
      date: getHeader('date'),
      body: bodyForLLM, // Processed body (plain text or stripped HTML, truncated)
      body_plain: body.plainText, // Keep original for reference
      body_html: body.htmlText, // Keep original for reference
      snippet: gmailMessage.snippet || '',
      headers: {
        messageId: getHeader('message-id'),
        replyTo: getHeader('reply-to'),
        returnPath: getHeader('return-path')
      },
      attachments: attachments,
      labels: gmailMessage.labelIds || []
    };
  } catch (error) {
    console.error('Error formatting email for LLM:', error);
    return null;
  }
};

/**
 * Build LLM prompt for subscription classification
 * Only sends essential fields to reduce payload size
 */
// System message - maintains core intent but simplified
const SYSTEM_MESSAGE = `You are an expert subscription email classifier. Analyze emails and determine if they are REAL PAID subscription-related emails.

CRITICAL RULES:
1. ONLY track PAID subscriptions - if no payment/charge, set is_subscription=FALSE
2. IGNORE free trials without payment
3. ACCEPT: Paid invoices, cancellations of PAID subscriptions, renewals, payment failures
4. REJECT: Free trials, one-time purchases (Uber, hotels, retail), promotions

Return JSON array:

For REJECTED emails (is_subscription=false), return ONLY:
{"email_id": "from_input", "is_subscription": false}

For ACCEPTED subscriptions (is_subscription=true), return FULL data:
{
  "email_id": "from_input",
  "is_subscription": true,
  "confidence": 0-100,
  "classification_reason": "reason",
  "extracted_data": {
    "name": "Product",
    "service": "Company",
    "category": "Category",
    "price": 0,
    "currency": "USD",
    "billing_cycle": "monthly/yearly/quarterly/weekly",
    "status": "active/cancelled/paused",
    "subscription_date": "YYYY-MM-DD",
    "next_billing_date": "YYYY-MM-DD",
    "event_type": "invoice/cancellation/renewal/payment_failed/plan_change",
    "notes": "context"
  }
}

FIELD RULES (for accepted subscriptions only):
- Status: active/cancelled/paused (active for invoices, cancelled for cancellations)
- Event Type: invoice/cancellation/renewal/payment_failed/plan_change
- Billing Cycle: weekly/monthly/quarterly/yearly
- Currency: USD, EUR, GBP, AUD, CAD, etc. (3-letter codes)
- Categories: Streaming, Music, Software, Productivity, Cloud Storage, Design, Gaming, News & Media, Health & Fitness, Education, Communication, Development, AI Tools, Security, Professional, Books, Custom
- Dates: YYYY-MM-DD format`;

// Build user message with emails to analyze
const buildUserMessage = (emails) => {
  // Create minimal email objects for LLM - send full body for accurate classification
  const minimalEmails = emails.map(email => ({
    id: email.id,
    threadId: email.threadId,
    subject: email.subject,
    from: email.from,
    to: email.to,
    date: email.date,
    body: email.body  // Full body - no truncation for accurate classification
  }));

  return `Analyze these emails and return a JSON array with classification results:\n\n${JSON.stringify(minimalEmails, null, 2)}`;
};

/**
 * Classify using direct Groq API call (TEMPORARY - for testing)
 */
const classifyWithDirectGroq = async (emails) => {
  console.log(`   🔓 TEMP: Using direct Groq API call (browser mode)`);
  console.log(`   ⚠️  API key exposed in browser - move to Edge Function for production!`);

  // Build user message with emails
  const userMessage = buildUserMessage(emails);

  // Get current model based on tier mode
  let currentModelName;
  let maxTokens = 16000;

  if (currentTierMode === 'paid') {
    // PAID TIER: Use selected model, no rotation
    currentModelName = selectedPaidModel;
    const modelConfig = ALL_GROQ_MODELS.find(m => m.name === selectedPaidModel);
    maxTokens = modelConfig?.maxOutputTokens || 16000;
    console.log(`   💰 PAID TIER: Using ${currentModelName} (no rotation)`);
  } else {
    // FREE TIER: Use rotating models
    const currentModel = CONFIG.models[currentModelIndex];
    currentModelName = currentModel.name;
    console.log(`   🆓 FREE TIER: Using model ${currentModelIndex + 1}/${CONFIG.models.length}: ${currentModelName}`);
  }

  const estimatedTokens = estimatePromptTokens(emails);
  console.log(`   📊 Estimated tokens: ${estimatedTokens}`);
  console.log(`   🚀 Calling Groq API with ${emails.length} emails...`);

  // Calculate payload size for user message only (system message not counted in user payload)
  const payloadSize = new Blob([userMessage]).size;
  console.log(`   📦 User message size: ${(payloadSize / 1024).toFixed(2)} KB`);

  // Log full input to LLM
  console.log('\n📤 SYSTEM MESSAGE:');
  console.log(SYSTEM_MESSAGE);
  console.log('\n📤 USER MESSAGE:');
  console.log(userMessage);
  console.log('\n');

  let response;
  try {
    response = await fetch(CONFIG.groqApiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${CONFIG.groqApiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: currentModelName,
        messages: [
          {
            role: 'system',
            content: SYSTEM_MESSAGE
          },
          {
            role: 'user',
            content: userMessage
          }
        ],
        temperature: 0.1,
        max_tokens: maxTokens,
      })
    });
  } catch (fetchError) {
    console.error(`   ❌ FETCH ERROR:`, fetchError);
    throw new Error(`Network error calling Groq API: ${fetchError.message}`);
  }

  console.log(`   📡 Groq API response status: ${response.status} ${response.statusText}`);

  if (!response.ok) {
    const error = await response.text();
    console.error(`   ❌ GROQ API ERROR (${response.status}):`, error);

    if (response.status === 413) {
      console.error(`   💡 Payload too large! Current batch has ${emails.length} emails.`);
      console.error(`   💡 Try reducing REACT_APP_LLM_BATCH_SIZE in .env`);
    }

    if (response.status === 429) {
      console.error(`   ⏰ RATE LIMIT (429) on ${currentModelName}!`);

      // Only rotate models in FREE tier
      if (currentTierMode === 'free') {
        const previousModelName = currentModelName;
        const previousIndex = currentModelIndex;
        currentModelIndex = (currentModelIndex + 1) % CONFIG.models.length;
        const nextModelName = CONFIG.models[currentModelIndex].name;
        console.error(`   🔄 Model ${previousIndex + 1}/${CONFIG.models.length} (${previousModelName}) → Model ${currentModelIndex + 1}/${CONFIG.models.length} (${nextModelName})`);
      } else {
        console.error(`   💰 PAID TIER: No rotation - will retry with same model after delay`);
      }
    }

    if (response.status === 400) {
      console.error(`   ❌ BAD REQUEST (400) on ${currentModelName} - model may not be available`);

      // Only rotate models in FREE tier
      if (currentTierMode === 'free') {
        const previousModelName = currentModelName;
        const previousIndex = currentModelIndex;
        currentModelIndex = (currentModelIndex + 1) % CONFIG.models.length;
        const nextModelName = CONFIG.models[currentModelIndex].name;
        console.error(`   🔄 Model ${previousIndex + 1}/${CONFIG.models.length} (${previousModelName}) → Model ${currentModelIndex + 1}/${CONFIG.models.length} (${nextModelName})`);
      } else {
        console.error(`   💰 PAID TIER: No rotation - check model configuration`);
      }
    }

    if (response.status === 413) {
      console.error(`   ❌ PAYLOAD TOO LARGE (413) on ${currentModelName} - batch exceeds token limit`);
      console.error(`   📊 Estimated: ${estimatedTokens} tokens`);

      // Only rotate models in FREE tier
      if (currentTierMode === 'free') {
        const previousModelName = currentModelName;
        const previousIndex = currentModelIndex;
        currentModelIndex = (currentModelIndex + 1) % CONFIG.models.length;
        const nextModelName = CONFIG.models[currentModelIndex].name;
        console.error(`   🔄 Model ${previousIndex + 1}/${CONFIG.models.length} (${previousModelName}) → Model ${currentModelIndex + 1}/${CONFIG.models.length} (${nextModelName})`);
      } else {
        console.error(`   💰 PAID TIER: No rotation - reduce batch size`);
      }
    }

    const err = new Error(`Groq API error: ${response.status} - ${error}`);
    err.status = response.status;
    err.retryAfter = response.headers.get('Retry-After');
    throw err;
  }

  const data = await response.json();
  const content = data.choices[0]?.message?.content;

  if (!content) {
    throw new Error('Empty response from Groq API');
  }

  console.log('📥 RAW LLM RESPONSE:');
  console.log(content);
  console.log('\n');

  // Parse JSON
  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch (e) {
    console.warn('⚠️  First JSON parse failed, trying to extract from markdown code block...');
    const jsonMatch = content.match(/```json\n?([\s\S]*?)\n?```/);
    if (jsonMatch) {
      console.log('✓ Found JSON in markdown code block');
      parsed = JSON.parse(jsonMatch[1]);
    } else {
      // Try to find array pattern
      const arrayMatch = content.match(/\[[\s\S]*\]/);
      if (arrayMatch) {
        console.log('✓ Found array pattern in response');
        parsed = JSON.parse(arrayMatch[0]);
      } else {
        console.error('❌ Could not find valid JSON in response');
        console.error('Response content:', content.substring(0, 500));
        throw new Error('Could not parse JSON from LLM response');
      }
    }
  }

  const results = Array.isArray(parsed) ? parsed : (parsed.results || parsed.classifications || []);

  if (!Array.isArray(results)) {
    throw new Error('LLM response is not an array');
  }

  // Filter by confidence threshold
  const accepted = results
    .map((result, idx) => ({ ...result, original_email: emails[idx] }))
    .filter(result => result.is_subscription && result.confidence >= CONFIG.confidenceThreshold);

  const rejected = results
    .map((result, idx) => ({ ...result, original_email: emails[idx] }))
    .filter(result => !result.is_subscription || result.confidence < CONFIG.confidenceThreshold);

  // LOG OUTPUT: Raw JSON from LLM
  console.log('📥 RAW OUTPUT FROM LLM:');
  console.log(JSON.stringify(results, null, 2));

  // Pretty summary of accepted subscriptions
  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log(`✅ ACCEPTED SUBSCRIPTIONS (${accepted.length}/${results.length})`);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  if (accepted.length > 0) {
    accepted.forEach((result, idx) => {
      const data = result.extracted_data || {};
      console.log(`\n${idx + 1}. ${data.name || 'Unknown'}`);
      console.log(`   Company: ${data.service || 'N/A'}`);
      console.log(`   Price: ${data.currency || '$'}${data.price || '0'} / ${data.billing_cycle || 'month'}`);
      console.log(`   Status: ${data.status || 'N/A'}`);
      console.log(`   Event: ${data.event_type || 'N/A'}`);
      console.log(`   Next billing: ${data.next_billing_date || 'N/A'}`);
      console.log(`   Confidence: ${result.confidence}%`);
    });
  } else {
    console.log('\n   (No subscriptions found)');
  }

  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log(`❌ REJECTED (${rejected.length}/${results.length})`);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  if (rejected.length > 0) {
    rejected.forEach((result, idx) => {
      const email = result.original_email || {};
      console.log(`\n${idx + 1}. ${email.subject || '(no subject)'}`);
      console.log(`   From: ${email.from || 'N/A'}`);
      console.log(`   Reason: ${result.classification_reason || 'N/A'}`);
      console.log(`   Confidence: ${result.confidence}%`);
    });
  } else {
    console.log('\n   (All emails accepted)');
  }

  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

  return { accepted, rejected, totalProcessed: results.length };
};

/**
 * Classify a batch of emails using direct Groq API
 * NOTE: This is only used for the model comparison test in admin settings.
 * Production email sync uses the Python backend (classifierService.js)
 */
export const classifyEmailBatch = async (emails) => {
  try {
    console.log(`\n🤖 Sending ${emails.length} emails for classification...`);

    if (!CONFIG.groqApiKey) {
      throw new Error('Groq API key not configured. Set REACT_APP_GROQ_API_KEY in .env');
    }

    const data = await classifyWithDirectGroq(emails);

    console.log(`✅ Classification complete`);
    console.log(`   ✅ Accepted: ${data.accepted?.length || 0}`);
    console.log(`   ❌ Rejected: ${data.rejected?.length || 0}`);

    return data;

  } catch (error) {
    console.error('❌ Error classifying email batch:', error.message);
    throw error;
  }
};

/**
 * Filter all emails using LLM with batching and rate limiting
 * @param {Array} gmailMessages - Array of Gmail message objects
 * @param {Function} onBatchComplete - Optional callback(accepted, rejected) called after each batch
 */
export const filterEmailsWithLLM = async (gmailMessages, onBatchComplete = null) => {
  try {
    console.log(`\n🔮 Starting LLM-based email filtering...`);
    console.log(`   Total emails: ${gmailMessages.length}`);
    console.log(`   Batch size: ${CONFIG.batchSize}`);
    console.log(`   Confidence threshold: ${CONFIG.confidenceThreshold}%`);

    if (CONFIG.useDirectGroq && CONFIG.groqApiKey) {
      console.log(`   🔓 Mode: Direct Groq API (TEMPORARY - browser testing)`);
      console.log(`   ⚠️  For production, deploy Edge Function (see LLM_SETUP_GUIDE.md)`);
    } else {
      console.log(`   🔒 Mode: Secure Supabase Edge Function`);
    }

    // Format emails for LLM
    console.log(`\n📝 Formatting emails for LLM...`);
    const formattedEmails = gmailMessages
      .map(formatEmailForLLM)
      .filter(email => email !== null);

    console.log(`   Formatted ${formattedEmails.length} emails`);

    // Token-aware smart batching based on tier mode
    let MODEL_TOKEN_LIMIT;
    let TARGET_BATCH_SIZE;
    let modelName;

    if (currentTierMode === 'paid') {
      // PAID TIER: Larger batches, single model
      MODEL_TOKEN_LIMIT = PAID_TIER_CONFIG.maxBatchTokens;
      TARGET_BATCH_SIZE = PAID_TIER_CONFIG.batchSize; // 10 emails
      modelName = selectedPaidModel;
      console.log(`\n💰 PAID TIER BATCHING:`);
    } else {
      // FREE TIER: Smaller batches, model rotation
      const currentModel = CONFIG.models[currentModelIndex];
      MODEL_TOKEN_LIMIT = currentModel.tokensPerRequest;
      TARGET_BATCH_SIZE = FREE_TIER_CONFIG.batchSize; // 2 emails
      modelName = currentModel.name;
      console.log(`\n🆓 FREE TIER BATCHING:`);
    }

    // Reserve tokens for prompt overhead (instructions, JSON structure, etc.)
    const PROMPT_OVERHEAD_TOKENS = 800; // Rough estimate for our prompt template
    const MAX_EMAIL_TOKENS = MODEL_TOKEN_LIMIT - PROMPT_OVERHEAD_TOKENS;

    console.log(`   Tier: ${currentTierMode.toUpperCase()}`);
    console.log(`   Model: ${modelName}`);
    console.log(`   Token limit: ${MODEL_TOKEN_LIMIT} tokens/request`);
    console.log(`   Available for emails: ${MAX_EMAIL_TOKENS} tokens`);
    console.log(`   Target batch size: ${TARGET_BATCH_SIZE} emails\n`);

    const batches = [];
    let currentBatch = [];
    let currentBatchTokens = 0;

    for (const email of formattedEmails) {
      // Estimate tokens for this email (subject + body)
      const emailText = `${email.subject || ''} ${email.body || ''}`;
      const emailTokens = estimateTokens(emailText);

      // Check if adding this email would exceed limits
      const wouldExceedTokens = currentBatchTokens + emailTokens > MAX_EMAIL_TOKENS;
      const wouldExceedCount = currentBatch.length >= TARGET_BATCH_SIZE;

      if (currentBatch.length > 0 && (wouldExceedTokens || wouldExceedCount)) {
        // Current batch is full - save it and start new one
        console.log(`   ✓ Batch ${batches.length + 1}: ${currentBatch.length} emails, ~${currentBatchTokens} tokens`);
        batches.push(currentBatch);
        currentBatch = [];
        currentBatchTokens = 0;
      }

      // If single email exceeds token limit, warn but still process it
      if (emailTokens > MAX_EMAIL_TOKENS && currentBatch.length === 0) {
        console.log(`   ⚠️  Large email: ${emailTokens} tokens (exceeds ${MAX_EMAIL_TOKENS}) - sending as single-email batch`);
        batches.push([email]);
        continue;
      }

      // Add email to current batch
      currentBatch.push(email);
      currentBatchTokens += emailTokens;
    }

    // Add remaining emails
    if (currentBatch.length > 0) {
      console.log(`   ✓ Batch ${batches.length + 1}: ${currentBatch.length} emails, ~${currentBatchTokens} tokens`);
      batches.push(currentBatch);
    }

    console.log(`\n   📊 Created ${batches.length} batches (avg ${(formattedEmails.length / batches.length).toFixed(1)} emails/batch)\n`);

    const allAccepted = [];
    const allRejected = [];
    const startTime = Date.now();

    for (let i = 0; i < batches.length; i++) {
      // Calculate ETA
      const elapsed = Date.now() - startTime;
      const avgTimePerBatch = i > 0 ? elapsed / i : 0;
      const remainingBatches = batches.length - i;
      const etaMs = avgTimePerBatch * remainingBatches;
      const etaSeconds = Math.ceil(etaMs / 1000);

      console.log(`\n📦 Processing batch ${i + 1}/${batches.length}... ${i > 0 ? `ETA: ${etaSeconds}s` : ''}`);

      let modelsAttempted = 0;
      let batchResults = null;
      const maxModelAttempts = CONFIG.models.length; // Try all models once

      // Try all models in rotation before giving up
      while (modelsAttempted < maxModelAttempts && !batchResults) {
        try {
          batchResults = await classifyEmailBatch(batches[i]);
        } catch (error) {
          modelsAttempted++;

          // If rate limit (429), bad request (400), or payload too large (413), try next model immediately
          if ((error.status === 429 || error.status === 400 || error.status === 413) && modelsAttempted < maxModelAttempts) {
            console.log(`   🔄 Trying next model (${modelsAttempted}/${maxModelAttempts} models attempted)...`);
            // Model rotation already happened in classifyEmailBatch error handler
            // Continue immediately to next iteration without delay
          } else if (modelsAttempted >= maxModelAttempts) {
            console.error(`   ❌ Batch ${i + 1} failed - all ${maxModelAttempts} models exhausted`);
            throw error;
          } else {
            // For other errors (network, etc), retry with small delay
            console.log(`   ⚠️  Error: ${error.message}, retrying after 2s...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      if (batchResults) {
        allAccepted.push(...(batchResults.accepted || []));
        allRejected.push(...(batchResults.rejected || []));

        // Call the batch complete callback if provided (for immediate DB save)
        if (onBatchComplete && typeof onBatchComplete === 'function') {
          try {
            await onBatchComplete(batchResults.accepted, batchResults.rejected, i + 1, batches.length);
          } catch (callbackError) {
            console.error(`   ⚠️  Batch callback error:`, callbackError.message);
            // Don't fail the whole process if callback fails
          }
        }
      }

      // No delay needed - model rotation handles rate limits automatically
    }

    console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`✅ LLM FILTERING COMPLETE - FINAL SUMMARY`);
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`   📊 Total emails processed: ${allAccepted.length + allRejected.length}`);
    console.log(`   ✅ Accepted as subscriptions: ${allAccepted.length}`);
    console.log(`   ❌ Rejected (not subscriptions): ${allRejected.length}`);
    console.log(`   📈 Acceptance rate: ${((allAccepted.length / (allAccepted.length + allRejected.length)) * 100).toFixed(1)}%`);
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

    return {
      accepted: allAccepted,
      rejected: allRejected,
      totalProcessed: allAccepted.length + allRejected.length
    };

  } catch (error) {
    console.error('❌ Error in LLM filtering:', error);
    throw error;
  }
};

/**
 * Convert LLM extracted data to subscription DB format
 * Includes the original email data sent to LLM for future reference
 */
export const convertLLMToSubscriptionFormat = (llmResult, gmailAccountEmail, gmailMessageId) => {
  const data = llmResult.extracted_data;

  if (!data) {
    return null;
  }

  // Get the original email data that was sent to LLM
  const originalEmailData = llmResult.original_email || null;

  // Assign logo based on category since LLM doesn't return emojis (they break JSON parsing)
  const categoryLogos = {
    'Streaming': '🎬',
    'Music': '🎵',
    'Software': '💻',
    'Productivity': '📊',
    'Cloud Storage': '☁️',
    'Design': '🎨',
    'Gaming': '🎮',
    'News & Media': '📰',
    'Health & Fitness': '💪',
    'Education': '🎓',
    'Communication': '💬',
    'Development': '👨‍💻',
    'AI Tools': '🤖',
    'Security': '🔒',
    'Professional': '💼',
    'Books': '📚',
    'Custom': '📋'
  };

  const logo = categoryLogos[data.category] || '📋';

  return {
    name: data.name || 'Unknown Service',
    service: data.service || data.name,
    category: data.category || 'Other',
    logo: logo,
    price: parseFloat(data.price) || 0,
    currency: data.currency || 'USD',
    billing_cycle: data.billing_cycle || 'monthly',
    status: data.status || 'active',
    next_billing_date: data.next_billing_date || null,
    subscription_date: data.subscription_date || null,
    event_type: data.event_type || 'invoice',
    email_link: `https://mail.google.com/mail/u/0/#inbox/${gmailMessageId}`,
    import_source: 'gmail',
    imported_from_email: gmailAccountEmail,
    original_email_data: originalEmailData, // Store full email JSON for future reference
    notes: data.notes || `Imported from Gmail via LLM classification (${llmResult.confidence}% confidence). Reason: ${llmResult.classification_reason}`
  };
};

// ============================================================================
// MODEL COMPARISON TEST
// ============================================================================

/**
 * Classify emails with a specific model (for comparison test)
 */
const classifyWithSpecificModel = async (emails, modelName) => {
  const userMessage = buildUserMessage(emails);

  // Get model-specific max tokens (default to 8192 if not found)
  const modelConfig = ALL_GROQ_MODELS.find(m => m.name === modelName);
  const maxTokens = modelConfig?.maxOutputTokens || 8192;

  const response = await fetch(CONFIG.groqApiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${CONFIG.groqApiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: modelName,
      messages: [
        { role: 'system', content: SYSTEM_MESSAGE },
        { role: 'user', content: userMessage }
      ],
      temperature: 0.1,
      max_tokens: maxTokens,
    })
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Model ${modelName}: ${response.status} - ${error}`);
  }

  const data = await response.json();
  const content = data.choices[0]?.message?.content;

  if (!content) {
    throw new Error(`Model ${modelName}: Empty response`);
  }

  // Parse JSON response
  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch (e) {
    const jsonMatch = content.match(/```json\n?([\s\S]*?)\n?```/) || content.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      parsed = JSON.parse(jsonMatch[1] || jsonMatch[0]);
    } else {
      throw new Error(`Model ${modelName}: Could not parse JSON`);
    }
  }

  return Array.isArray(parsed) ? parsed : (parsed.results || parsed.classifications || []);
};

/**
 * Run model comparison test on test emails
 * Runs ALL Groq models on the same emails and compares results
 */
export const runModelComparisonTest = async (testEmails) => {
  if (!CONFIG.groqApiKey) {
    throw new Error('Groq API key not configured. Set REACT_APP_GROQ_API_KEY in .env');
  }

  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🧪 STARTING MODEL COMPARISON TEST');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log(`   📧 Test emails: ${testEmails.length}`);
  console.log(`   🤖 Models to test: ${ALL_GROQ_MODELS.length}`);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

  const results = [];

  for (const model of ALL_GROQ_MODELS) {
    console.log(`\n🔄 Testing model: ${model.name}...`);
    const startTime = Date.now();

    try {
      const classifications = await classifyWithSpecificModel(testEmails, model.name);
      const latency = Date.now() - startTime;

      // Calculate metrics
      const subscriptions = classifications.filter(c => c.is_subscription);
      const avgConfidence = subscriptions.length > 0
        ? subscriptions.reduce((sum, s) => sum + (s.confidence || 0), 0) / subscriptions.length
        : 0;

      // Estimate cost
      const inputTokens = estimatePromptTokens(testEmails);
      const outputTokens = estimateTokens(JSON.stringify(classifications));
      const estimatedCost = (inputTokens * model.inputCost / 1000000) + (outputTokens * model.outputCost / 1000000);

      // Get detected services
      const detectedServices = subscriptions
        .map(s => s.extracted_data?.service || s.extracted_data?.name)
        .filter(Boolean);

      const result = {
        model: model.name,
        success: true,
        latency,
        subscriptionsFound: subscriptions.length,
        avgConfidence: Math.round(avgConfidence),
        inputTokens,
        outputTokens,
        estimatedCost: estimatedCost.toFixed(6),
        detectedServices,
        classifications
      };

      results.push(result);

      console.log(`   ✅ ${model.name}: ${subscriptions.length} subs, ${Math.round(avgConfidence)}% conf, ${latency}ms, $${estimatedCost.toFixed(6)}`);

    } catch (error) {
      const latency = Date.now() - startTime;
      console.log(`   ❌ ${model.name}: ${error.message}`);

      results.push({
        model: model.name,
        success: false,
        error: error.message,
        latency,
        subscriptionsFound: 0,
        avgConfidence: 0,
        inputTokens: 0,
        outputTokens: 0,
        estimatedCost: '0.000000',
        detectedServices: [],
        classifications: []
      });
    }

    // Small delay between models to avoid rate limits
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🏁 MODEL COMPARISON TEST COMPLETE');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  // Summary table
  console.log('\n📊 RESULTS SUMMARY:\n');
  console.log('Model                          | Subs | Conf | Latency | Cost');
  console.log('-------------------------------|------|------|---------|--------');
  results.forEach(r => {
    const name = r.model.padEnd(30);
    const subs = String(r.subscriptionsFound).padStart(4);
    const conf = (r.success ? `${r.avgConfidence}%` : 'ERR').padStart(4);
    const lat = (r.success ? `${r.latency}ms` : 'ERR').padStart(7);
    const cost = `$${r.estimatedCost}`;
    console.log(`${name} | ${subs} | ${conf} | ${lat} | ${cost}`);
  });

  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

  return results;
};

export default {
  formatEmailForLLM,
  classifyEmailBatch,
  filterEmailsWithLLM,
  convertLLMToSubscriptionFormat,
  // Tier mode API
  getTierMode,
  setTierMode,
  getSelectedPaidModel,
  setSelectedPaidModel,
  getAllGroqModels,
  getCurrentTierConfig,
  // Model comparison
  runModelComparisonTest
};
